import { useState } from "react";
import { Folder, Film, FolderOpen, Copy, Trash2, MoreHorizontal, FolderInput, Pencil } from "lucide-react";
import { PTXT, fmtDate } from "./projectsText";

export function Counters({ stats, lang }) {
  const t = PTXT[lang] || PTXT.fr;
  const h = stats?.hours_per_video ?? 1;
  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-5 mb-10" data-testid="projects-counters">
      <div className="bg-card border border-border p-5 sm:p-6">
        <div className="font-display text-3xl sm:text-4xl text-foreground" data-testid="counter-videos">{stats ? stats.videos_created : "—"}</div>
        <div className="font-osd text-[11px] tracking-[0.14em] uppercase text-muted-foreground mt-2">{t.videos}</div>
      </div>
      <div className="bg-card border border-border p-5 sm:p-6" title={t.hoursTip(h)} data-testid="counter-hours-card">
        <div className="font-display text-3xl sm:text-4xl text-foreground" data-testid="counter-hours">{stats ? `${stats.hours_saved} h` : "—"}</div>
        <div className="font-osd text-[11px] tracking-[0.14em] uppercase text-muted-foreground mt-2 flex items-center gap-1.5">
          {t.hours}
          <span className="inline-grid place-items-center h-4 w-4 rounded-full border border-border text-[9px] cursor-help" aria-label={t.hoursTip(h)} data-testid="counter-hours-info">?</span>
        </div>
      </div>
    </div>
  );
}

export function FolderCard({ folder, lang, onOpen, onRename, onDelete, onDropProject, onDropFolder }) {
  const t = PTXT[lang] || PTXT.fr;
  const [over, setOver] = useState(false);
  const drop = (e) => {
    e.preventDefault(); setOver(false);
    const pid = e.dataTransfer.getData("bc/project");
    const fid = e.dataTransfer.getData("bc/folder");
    if (pid) onDropProject(pid, folder.folder_id);
    else if (fid && fid !== folder.folder_id) onDropFolder(fid, folder.folder_id);
  };
  return (
    <div
      className={`bg-card border p-4 flex items-center gap-3 group cursor-pointer transition-colors ${over ? "border-primary" : "border-border hover:border-foreground"}`}
      draggable
      onDragStart={(e) => e.dataTransfer.setData("bc/folder", folder.folder_id)}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={drop}
      onClick={() => onOpen(folder.folder_id)}
      data-testid={`folder-card-${folder.folder_id}`}
    >
      <Folder size={18} className="text-primary shrink-0" />
      <span className="flex-1 truncate font-display font-bold text-sm">{folder.name}</span>
      <button onClick={(e) => { e.stopPropagation(); onRename(folder); }} title={t.rename} data-testid={`folder-rename-${folder.folder_id}`} className="p-2 text-muted-foreground hover:text-foreground"><Pencil size={13} /></button>
      <button onClick={(e) => { e.stopPropagation(); onDelete(folder); }} title={t.remove} data-testid={`folder-delete-${folder.folder_id}`} className="p-2 text-muted-foreground hover:text-primary"><Trash2 size={13} /></button>
    </div>
  );
}

export function ProjectCard({ p, lang, onOpen, onRename, onDuplicate, onMove, onDelete }) {
  const t = PTXT[lang] || PTXT.fr;
  const [menu, setMenu] = useState(false);
  return (
    <div
      className="bg-card border border-border overflow-hidden group relative"
      draggable
      onDragStart={(e) => e.dataTransfer.setData("bc/project", p.project_id)}
      data-testid={`project-card-${p.project_id}`}
    >
      <button onClick={() => onOpen(p)} className="block w-full aspect-[9/12] bg-secondary/40 relative overflow-hidden rounded-none" data-testid={`project-open-thumb-${p.project_id}`}>
        {p.thumb ? (
          <img src={p.thumb} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
        ) : (
          <span className="absolute inset-0 flex items-center justify-center text-muted-foreground"><Film size={32} /></span>
        )}
        <span className="absolute left-3 bottom-3 font-osd text-[10px] tracking-wider uppercase bg-background/85 px-2 py-1 text-foreground" data-testid={`project-exports-${p.project_id}`}>
          {t.exports(p.exports || 0)}
        </span>
      </button>
      <div className="p-4">
        <button onClick={() => onRename(p)} className="font-display font-bold text-left w-full truncate hover:text-primary transition-colors" title={t.rename} data-testid={`project-title-${p.project_id}`}>
          {p.title}
        </button>
        <p className="text-xs text-muted-foreground mt-1">{t.modified} {fmtDate(p.updated_at, lang)}</p>
        <div className="flex gap-2 mt-4">
          <button onClick={() => onOpen(p)} data-testid={`project-open-${p.project_id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-primary text-white text-xs font-bold px-3 h-10 hover:opacity-90 transition-opacity">
            <FolderOpen size={13} /> {t.open}
          </button>
          <button onClick={() => setMenu(!menu)} data-testid={`project-menu-${p.project_id}`} className="border border-border px-3 h-10 hover:border-foreground transition-colors" title="…"><MoreHorizontal size={14} /></button>
        </div>
        {menu && (
          <div className="mt-2 border border-border bg-popover divide-y divide-border" data-testid={`project-menu-list-${p.project_id}`}>
            <MenuItem icon={FolderInput} label={t.move} testid={`project-move-${p.project_id}`} onClick={() => { setMenu(false); onMove(p); }} />
            <MenuItem icon={Copy} label={t.duplicate} testid={`project-duplicate-${p.project_id}`} onClick={() => { setMenu(false); onDuplicate(p); }} />
            <MenuItem icon={Trash2} label={t.remove} testid={`project-delete-${p.project_id}`} danger onClick={() => { setMenu(false); onDelete(p); }} />
          </div>
        )}
      </div>
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, testid, danger }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`w-full flex items-center gap-2.5 px-3 h-11 text-xs text-left hover:bg-secondary transition-colors ${danger ? "text-primary" : "text-foreground"}`}>
      <Icon size={13} /> {label}
    </button>
  );
}

export function MoveDialog({ open, folders, current, lang, onClose, onPick }) {
  const t = PTXT[lang] || PTXT.fr;
  if (!open) return null;
  const rows = [];
  const walk = (parent, depth) => {
    folders.filter((f) => (f.parent_id || null) === parent).forEach((f) => { rows.push({ ...f, depth }); walk(f.folder_id, depth + 1); });
  };
  walk(null, 0);
  return (
    <div className="fixed inset-0 z-[9000] bg-black/80 flex items-end sm:items-center justify-center p-3" role="dialog" aria-modal="true" data-testid="move-dialog">
      <div className="w-full max-w-sm bg-card border border-border p-6 max-h-[80vh] flex flex-col">
        <p className="font-osd text-xs tracking-[0.25em] text-primary mb-4">[ {t.moveTitle.toUpperCase()} ]</p>
        <div className="flex-1 overflow-y-auto divide-y divide-border border border-border">
          <button onClick={() => onPick(null)} data-testid="move-to-root" className={`w-full text-left px-3 h-12 text-sm flex items-center gap-2 hover:bg-secondary ${current == null ? "text-primary" : ""}`}><Folder size={14} /> {t.moveRoot}</button>
          {rows.map((f) => (
            <button key={f.folder_id} onClick={() => onPick(f.folder_id)} data-testid={`move-to-${f.folder_id}`} style={{ paddingLeft: 12 + f.depth * 16 }} className={`w-full text-left pr-3 h-12 text-sm flex items-center gap-2 hover:bg-secondary ${current === f.folder_id ? "text-primary" : ""}`}>
              <Folder size={14} /> <span className="truncate">{f.name}</span>
            </button>
          ))}
        </div>
        <button onClick={onClose} data-testid="move-cancel" className="mt-4 h-11 border border-border font-osd text-[11px] tracking-[0.08em] uppercase">{t.cancel}</button>
      </div>
    </div>
  );
}
